import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Sparkles, Lock, X, Plus, Loader2, BarChart3,
  BookOpen, Star, ChevronRight, ChevronLeft, LogOut, Settings, Mail, GraduationCap, Pencil
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { AVATARS, AvatarDisplay } from '@/lib/avatars';
import TrialBanner from '@/components/TrialBanner';
import LockedAccount from '@/components/LockedAccount';
import WeeklyCatchupsTile from '@/components/parent/WeeklyCatchupsTile';

const LOGO_ICON = 'https://static.prod-images.emergentagent.com/jobs/85ba9bbd-736c-4dbb-ae2d-37b84f64fb5b/images/46c4c99dc82a03ab2b247ccff337db0bc40f8a2e674401b4d8deca221940954b.png';

export default function FamilyHome() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parent, setParent] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinAction, setPinAction] = useState(null); // 'dashboard' | 'add-child' | 'settings' | 'change-pin'
  const [pin, setPin] = useState(['', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChild, setNewChild] = useState({ name: '', date_of_birth: '', avatar: '', gender: '', board: '', grade: '', language_preference: 'auto', interests: [], hobbies: [] });
  const [isCreating, setIsCreating] = useState(false);
  const [addChildStep, setAddChildStep] = useState('basic'); // 'basic' | 'interests'

  // Edit Child state
  const [showEditChild, setShowEditChild] = useState(false);
  const [editChild, setEditChild] = useState(null);
  const [editChildStep, setEditChildStep] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingEditChildId, setPendingEditChildId] = useState(null);

  // Forgot PIN state
  const [forgotPinMode, setForgotPinMode] = useState(false); // 'sent' or false
  const [forgotPinLoading, setForgotPinLoading] = useState(false);
  const [tempPin, setTempPin] = useState(['', '', '', '']);

  // Change PIN state
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [oldPin, setOldPin] = useState(['', '', '', '']);
  const [newPin, setNewPin] = useState(['', '', '', '']);
  const [confirmNewPin, setConfirmNewPin] = useState(['', '', '', '']);
  const [changePinStep, setChangePinStep] = useState('old'); // 'old' | 'new' | 'confirm'
  const [changePinLoading, setChangePinLoading] = useState(false);

  // Link Child state
  const [showLinkChild, setShowLinkChild] = useState(false);
  const [linkCode, setLinkCode] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  // Trial state
  const [trialInfo, setTrialInfo] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [childrenRes, meRes] = await Promise.all([
        api.get('/children').catch(() => ({ data: [] })),
        api.get('/auth/me'),
      ]);
      setParent(meRes.data);

      // Check if account is locked
      if (meRes.data.is_locked) {
        setIsLocked(true);
        setLoading(false);
        return;
      }

      setChildren(childrenRes.data);

      // If no PIN set, redirect to set-pin
      if (!meRes.data.has_pin) {
        navigate('/set-pin', { replace: true });
        return;
      }

      // Fetch trial status
      try {
        const trialRes = await api.get('/auth/trial-status');
        setTrialInfo(trialRes.data);
        if (trialRes.data.is_locked) {
          setIsLocked(true);
        }
      } catch {}
    } catch (err) {
      // Handle locked account from API 403
      if (err?.response?.status === 403 && err?.response?.data?.detail?.includes('Trial expired')) {
        try {
          const meRes = await api.get('/auth/me');
          setParent(meRes.data);
          setIsLocked(true);
        } catch {
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const openPinFor = (action) => {
    setShowProfileMenu(false);
    setPinAction(action);
    setShowPinModal(true);
    setPin(['', '', '', '']);
    setForgotPinMode(false);
    setTempPin(['', '', '', '']);
    setTimeout(() => document.getElementById('fh-pin-0')?.focus(), 200);
  };

  const handlePinInput = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const arr = [...pin];
    arr[index] = value;
    setPin(arr);
    if (value && index < 3) document.getElementById(`fh-pin-${index + 1}`)?.focus();
    if (value && index === 3) {
      const full = arr.join('');
      if (full.length === 4) verifyPin(full);
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      document.getElementById(`fh-pin-${index - 1}`)?.focus();
    }
  };

  const verifyPin = async (pinStr) => {
    setVerifying(true);
    try {
      const res = await api.post('/auth/verify-pin', { pin: pinStr });
      if (res.data.valid) {
        setShowPinModal(false);
        if (pinAction === 'dashboard') navigate('/parent/home');
        else if (pinAction === 'add-child') setShowAddChild(true);
        else if (pinAction === 'edit-child') {
          const child = children.find(c => c.child_id === pendingEditChildId);
          if (child) {
            setEditChild({
              child_id: child.child_id,
              name: child.name || '',
              date_of_birth: child.date_of_birth || '',
              avatar: child.avatar || '',
              gender: child.gender || '',
              board: child.board || '',
              grade: child.grade || '',
              language_preference: child.language_preference || 'auto',
              interests: child.interests || [],
              hobbies: child.hobbies || [],
            });
            setEditChildStep('basic');
            setShowEditChild(true);
          }
          setPendingEditChildId(null);
        }
        else if (pinAction === 'settings') navigate('/parent/home');
      } else {
        toast.error('Incorrect PIN');
        setPin(['', '', '', '']);
        setTimeout(() => document.getElementById('fh-pin-0')?.focus(), 100);
      }
    } catch {
      toast.error('Failed to verify PIN');
      setPin(['', '', '', '']);
    } finally {
      setVerifying(false);
    }
  };

  const handleCreateChild = async (e) => {
    e.preventDefault();
    if (!newChild.name.trim()) { toast.error("Enter your child's name"); return; }
    if (!newChild.date_of_birth) { toast.error('Enter date of birth'); return; }
    if (!newChild.board) { toast.error('Please select board'); return; }
    if (!newChild.grade) { toast.error('Please select class'); return; }
    if (!newChild.avatar) { toast.error('Pick an avatar'); return; }
    setIsCreating(true);
    try {
      await api.post('/children', {
        name: newChild.name,
        date_of_birth: newChild.date_of_birth,
        avatar: newChild.avatar,
        gender: newChild.gender || null,
        board: newChild.board,
        grade: newChild.grade,
        language_preference: newChild.language_preference,
        interests: newChild.interests,
        hobbies: newChild.hobbies,
      });
      toast.success(`${newChild.name}'s profile created!`);
      setShowAddChild(false);
      setAddChildStep('basic');
      setNewChild({ name: '', date_of_birth: '', avatar: '', gender: '', board: '', grade: '', language_preference: 'auto', interests: [], hobbies: [] });
      fetchData();
    } catch {
      toast.error('Failed to create profile');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditChildClick = (e, childId) => {
    e.stopPropagation();
    setPendingEditChildId(childId);
    openPinFor('edit-child');
  };

  const handleSaveChild = async () => {
    if (!editChild) return;
    if (!editChild.name.trim()) { toast.error("Enter your child's name"); return; }
    if (!editChild.board) { toast.error('Please select board'); return; }
    if (!editChild.grade) { toast.error('Please select class'); return; }
    setIsSaving(true);
    try {
      await api.patch(`/children/${editChild.child_id}`, {
        name: editChild.name,
        date_of_birth: editChild.date_of_birth || undefined,
        avatar: editChild.avatar || undefined,
        gender: editChild.gender || undefined,
        board: editChild.board,
        grade: editChild.grade,
        language_preference: editChild.language_preference,
        interests: editChild.interests,
        hobbies: editChild.hobbies,
      });
      toast.success(`${editChild.name}'s profile updated!`);
      setShowEditChild(false);
      setEditChild(null);
      fetchData();
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try { await api.post('/auth/logout', {}); } catch {}
    localStorage.removeItem('yukti_session_token');
    navigate('/', { replace: true });
  };

  const handleLinkChild = async () => {
    if (!linkCode.trim()) { toast.error('Please enter the family code'); return; }
    setLinkLoading(true);
    try {
      const res = await api.post('/auth/link-child', { family_code: linkCode.trim() });
      toast.success(res.data.message);
      setShowLinkChild(false);
      setLinkCode('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLinkLoading(false);
    }
  };

  // --- Forgot PIN handlers ---
  const handleForgotPin = async () => {
    setForgotPinLoading(true);
    try {
      const res = await api.post('/auth/forgot-pin', {});
      toast.success(res.data.message);
      setForgotPinMode('sent');
      setTempPin(['', '', '', '']);
      setTimeout(() => document.getElementById('temp-pin-0')?.focus(), 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send PIN. Try again.');
    } finally {
      setForgotPinLoading(false);
    }
  };

  const handleTempPinInput = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const arr = [...tempPin];
    arr[index] = value;
    setTempPin(arr);
    if (value && index < 3) document.getElementById(`temp-pin-${index + 1}`)?.focus();
    if (value && index === 3) {
      const full = arr.join('');
      if (full.length === 4) verifyTempPin(full);
    }
  };

  const handleTempPinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !tempPin[index] && index > 0) {
      document.getElementById(`temp-pin-${index - 1}`)?.focus();
    }
  };

  const verifyTempPin = async (pinStr) => {
    setVerifying(true);
    try {
      const res = await api.post('/auth/verify-temp-pin', { pin: pinStr });
      if (res.data.valid) {
        toast.success('PIN verified! Please set a new PIN.');
        setShowPinModal(false);
        setForgotPinMode(false);
        // Open change PIN flow to set a brand new PIN
        openChangePinModal();
      } else {
        toast.error('Incorrect temporary PIN');
        setTempPin(['', '', '', '']);
        setTimeout(() => document.getElementById('temp-pin-0')?.focus(), 100);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
      setTempPin(['', '', '', '']);
    } finally {
      setVerifying(false);
    }
  };

  // --- Change PIN handlers ---
  const openChangePinModal = () => {
    setShowProfileMenu(false);
    setShowChangePinModal(true);
    setChangePinStep('old');
    setOldPin(['', '', '', '']);
    setNewPin(['', '', '', '']);
    setConfirmNewPin(['', '', '', '']);
    setTimeout(() => document.getElementById('change-pin-0')?.focus(), 200);
  };

  const getChangePinArr = () => {
    if (changePinStep === 'old') return oldPin;
    if (changePinStep === 'new') return newPin;
    return confirmNewPin;
  };

  const handleChangePinInput = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const setter = changePinStep === 'old' ? setOldPin : changePinStep === 'new' ? setNewPin : setConfirmNewPin;
    const arr = [...getChangePinArr()];
    arr[index] = value;
    setter(arr);
    if (value && index < 3) document.getElementById(`change-pin-${index + 1}`)?.focus();
    if (value && index === 3) {
      const full = arr.join('');
      if (full.length === 4) handleChangePinStep(full);
    }
  };

  const handleChangePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !getChangePinArr()[index] && index > 0) {
      document.getElementById(`change-pin-${index - 1}`)?.focus();
    }
  };

  const handleChangePinStep = async (full) => {
    if (changePinStep === 'old') {
      // Move to new PIN step
      setChangePinStep('new');
      setNewPin(['', '', '', '']);
      setTimeout(() => document.getElementById('change-pin-0')?.focus(), 100);
    } else if (changePinStep === 'new') {
      // Move to confirm step
      setChangePinStep('confirm');
      setConfirmNewPin(['', '', '', '']);
      setTimeout(() => document.getElementById('change-pin-0')?.focus(), 100);
    } else {
      // Confirm step — verify match and submit
      const newPinStr = newPin.join('');
      if (full !== newPinStr) {
        toast.error('PINs do not match. Try again.');
        setChangePinStep('new');
        setNewPin(['', '', '', '']);
        setConfirmNewPin(['', '', '', '']);
        setTimeout(() => document.getElementById('change-pin-0')?.focus(), 100);
        return;
      }
      setChangePinLoading(true);
      try {
        await api.post('/auth/change-pin', { old_pin: oldPin.join(''), new_pin: newPinStr });
        toast.success('PIN changed successfully!');
        setShowChangePinModal(false);
      } catch (err) {
        const detail = err.response?.data?.detail || 'Failed to change PIN';
        toast.error(detail);
        if (detail.includes('incorrect')) {
          setChangePinStep('old');
          setOldPin(['', '', '', '']);
        }
        setTimeout(() => document.getElementById('change-pin-0')?.focus(), 100);
      } finally {
        setChangePinLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF7ED 50%, #FEF3C7 100%)' }}>
        <Loader2 className="w-10 h-10 text-[#C2410C] animate-spin" />
      </div>
    );
  }

  if (isLocked) {
    return <LockedAccount parent={parent} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen font-[Nunito]" style={{ background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF7ED 50%, #FEF3C7 100%)' }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-orange-100/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_ICON} alt="Yukti" className="w-8 h-8" />
            <span className="text-xl font-bold text-[#1F2937] font-[Outfit]">Yukti</span>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 hover:bg-orange-50 rounded-full pl-3 pr-1 py-1 transition-colors"
              data-testid="parent-profile-btn"
            >
              <span className="text-sm font-medium text-[#4B5563] hidden sm:block">{parent?.name?.split(' ')[0]}</span>
              {parent?.picture ? (
                <img src={parent.picture} alt={parent.name} className="w-9 h-9 rounded-full border-2 border-[#C2410C]/20" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C2410C] to-[#EA580C] flex items-center justify-center text-white text-xs font-bold border-2 border-[#C2410C]/20">
                  {getInitials(parent?.name)}
                </div>
              )}
            </button>
            <AnimatePresence>
              {showProfileMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-xl border border-orange-100 overflow-hidden z-50"
                  data-testid="profile-dropdown"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-[#1F2937]">{parent?.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{parent?.email}</p>
                  </div>
                  <div className="py-1">
                    <button onClick={() => openPinFor('dashboard')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#4B5563] hover:bg-orange-50 transition-colors" data-testid="menu-parent-dashboard">
                      <BarChart3 className="w-4 h-4 text-[#C2410C]" /> Parent Dashboard <Lock className="w-3 h-3 text-[#9CA3AF] ml-auto" />
                    </button>
                    <button onClick={() => openPinFor('settings')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#4B5563] hover:bg-orange-50 transition-colors" data-testid="menu-settings">
                      <Settings className="w-4 h-4 text-[#C2410C]" /> Settings <Lock className="w-3 h-3 text-[#9CA3AF] ml-auto" />
                    </button>
                    <button onClick={openChangePinModal} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#4B5563] hover:bg-orange-50 transition-colors" data-testid="menu-change-pin">
                      <Lock className="w-4 h-4 text-[#C2410C]" /> Change PIN
                    </button>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors" data-testid="menu-logout">
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <p className="text-sm text-[#C2410C] font-medium mb-1">{getGreeting()}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-[Outfit]">Who's exploring today?</h1>
        </motion.div>

        {/* Trial Period Banner */}
        {trialInfo && !trialInfo.is_expired && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-6">
            <TrialBanner trialEnd={trialInfo.trial_end} />
          </motion.div>
        )}

        {/* CUM-4: Catch-Up Missions weekly tile, one per child (auto-hides when empty) */}
        {children.length > 0 && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {children.map((child) => (
              <WeeklyCatchupsTile
                key={`catchups-${child.child_id}`}
                childId={child.child_id}
                childName={child.name}
              />
            ))}
          </div>
        )}

        {/* Children Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {children.map((child, i) => (
              <motion.div
                key={child.child_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.08 }}
                whileHover={{ scale: 1.03, y: -4 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate(`/child/${child.child_id}/home`)}
                className="bg-white rounded-2xl p-5 sm:p-6 cursor-pointer text-center shadow-md hover:shadow-xl transition-all border border-orange-100 hover:border-[#C2410C]/30 group"
                data-testid={`child-card-${child.child_id}`}
              >
                <div className="flex justify-center mb-3">
                  <div className="relative">
                    <AvatarDisplay avatarId={child.avatar} size="lg" name={child.name} />
                    {child.streak > 0 && (
                      <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm">
                        <Star className="w-2.5 h-2.5 fill-white" />{child.streak}
                      </div>
                    )}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#1F2937] mb-0.5">{child.name}</h3>
                <p className="text-xs text-[#9CA3AF] mb-2">{[child.grade, child.board].filter(Boolean).join(' · ') || ''}</p>
                <div className="flex items-center justify-center gap-3 text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[#C2410C] flex items-center gap-1">Start learning <ChevronRight className="w-3 h-3" /></span>
                  <button
                    onClick={(e) => handleEditChildClick(e, child.child_id)}
                    className="flex items-center gap-1 text-[#9CA3AF] hover:text-[#C2410C] transition-colors"
                    data-testid={`edit-child-${child.child_id}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                </div>
              </motion.div>
            ))}

            {/* Add Child Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + children.length * 0.08 }}
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => openPinFor('add-child')}
              className="bg-white/60 rounded-2xl p-5 sm:p-6 cursor-pointer text-center border-2 border-dashed border-orange-200 hover:border-[#C2410C]/40 hover:bg-white transition-all flex flex-col items-center justify-center min-h-[200px]"
              data-testid="add-child-card"
            >
              <div className="w-14 h-14 rounded-full bg-[#C2410C]/10 flex items-center justify-center mb-3">
                <Plus className="w-6 h-6 text-[#C2410C]" />
              </div>
              <p className="text-sm font-semibold text-[#C2410C]">Add Child</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1 flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" /> Requires PIN
              </p>
            </motion.div>

            {/* Link Existing Child Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + children.length * 0.08 }}
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowLinkChild(true)}
              className="bg-white/60 rounded-2xl p-5 sm:p-6 cursor-pointer text-center border-2 border-dashed border-purple-200 hover:border-purple-400/40 hover:bg-white transition-all flex flex-col items-center justify-center min-h-[200px]"
              data-testid="link-child-card"
            >
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mb-3">
                <GraduationCap className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-sm font-semibold text-purple-600">Link Child's Account</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Enter their family code</p>
            </motion.div>
          </div>

          {children.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-center mt-4">
              <p className="text-[#9CA3AF] text-sm">No children added yet. Tap "Add Child" to get started!</p>
            </motion.div>
          )}
        </motion.div>

        {/* Quick Stats Banner */}
        {children.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-orange-100 flex items-center gap-4 overflow-x-auto"
          >
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-[#C2410C]/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#C2410C]" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF]">Family Activity</p>
                <p className="text-sm font-bold text-[#1F2937]">{children.length} learner{children.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="w-px h-8 bg-orange-100 flex-shrink-0" />
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <Star className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF]">Total XP</p>
                <p className="text-sm font-bold text-[#1F2937]">{children.reduce((s, c) => s + (c.xp || 0), 0)} points</p>
              </div>
            </div>
            <div className="w-px h-8 bg-orange-100 flex-shrink-0" />
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF]">Active Streaks</p>
                <p className="text-sm font-bold text-[#1F2937]">{children.filter(c => c.streak > 0).length} going</p>
              </div>
            </div>
            <div className="ml-auto flex-shrink-0">
              <Button
                onClick={() => openPinFor('dashboard')}
                variant="outline"
                className="border-[#C2410C]/20 text-[#C2410C] hover:bg-[#C2410C]/5 rounded-full text-xs font-semibold px-4"
                data-testid="view-insights-btn"
              >
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> View Insights <Lock className="w-3 h-3 ml-1.5 text-[#9CA3AF]" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* PIN Modal */}
      <AnimatePresence>
        {showPinModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            onClick={() => { setShowPinModal(false); setForgotPinMode(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl relative"
              data-testid="pin-modal"
            >
              <button onClick={() => { setShowPinModal(false); setForgotPinMode(false); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" data-testid="close-pin-modal">
                <X className="w-5 h-5" />
              </button>

              {!forgotPinMode ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-[#C2410C] to-[#EA580C] rounded-2xl flex items-center justify-center shadow-md">
                      <Lock className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1F2937] font-[Outfit]">Enter Parent PIN</h3>
                    <p className="text-[#9CA3AF] text-sm mt-1">4 digit PIN to access parent area</p>
                  </div>
                  <div className="flex justify-center gap-4 mb-4">
                    {[0, 1, 2, 3].map((i) => (
                      <input
                        key={i} id={`fh-pin-${i}`}
                        type="password" inputMode="numeric" maxLength={1}
                        value={pin[i]}
                        onChange={(e) => handlePinInput(i, e.target.value)}
                        onKeyDown={(e) => handlePinKeyDown(i, e)}
                        disabled={verifying}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/20 focus:outline-none transition-all disabled:opacity-50 bg-[#FFFBF5]"
                        data-testid={`verify-pin-${i}`}
                      />
                    ))}
                  </div>
                  {verifying && (
                    <div className="flex justify-center mb-3"><Loader2 className="w-6 h-6 text-[#C2410C] animate-spin" /></div>
                  )}
                  <div className="text-center">
                    <button
                      onClick={handleForgotPin}
                      disabled={forgotPinLoading}
                      className="text-sm text-[#C2410C] hover:text-[#9A3412] font-medium transition-colors disabled:opacity-50"
                      data-testid="forgot-pin-btn"
                    >
                      {forgotPinLoading ? 'Sending...' : 'Forgot PIN?'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-md">
                      <Mail className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1F2937] font-[Outfit]">Check Your Email</h3>
                    <p className="text-[#9CA3AF] text-sm mt-1">Enter the temporary PIN we sent to your email</p>
                    <p className="text-[#C2410C] text-xs mt-2 font-medium">Expires in 10 minutes</p>
                  </div>
                  <div className="flex justify-center gap-4 mb-4">
                    {[0, 1, 2, 3].map((i) => (
                      <input
                        key={i} id={`temp-pin-${i}`}
                        type="text" inputMode="numeric" maxLength={1}
                        value={tempPin[i]}
                        onChange={(e) => handleTempPinInput(i, e.target.value)}
                        onKeyDown={(e) => handleTempPinKeyDown(i, e)}
                        disabled={verifying}
                        className="w-14 h-16 text-center text-2xl font-bold border-2 border-green-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none transition-all disabled:opacity-50 bg-green-50/30"
                        data-testid={`temp-pin-${i}`}
                      />
                    ))}
                  </div>
                  {verifying && (
                    <div className="flex justify-center mb-3"><Loader2 className="w-6 h-6 text-green-600 animate-spin" /></div>
                  )}
                  <div className="text-center">
                    <button
                      onClick={() => { setForgotPinMode(false); setPin(['','','','']); setTimeout(() => document.getElementById('fh-pin-0')?.focus(), 200); }}
                      className="text-sm text-[#9CA3AF] hover:text-[#4B5563] font-medium transition-colors"
                      data-testid="back-to-pin-btn"
                    >
                      Back to PIN entry
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change PIN Modal */}
      <AnimatePresence>
        {showChangePinModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            onClick={() => setShowChangePinModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl relative"
              data-testid="change-pin-modal"
            >
              <button onClick={() => setShowChangePinModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" data-testid="close-change-pin">
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-[#C2410C] to-[#EA580C] rounded-2xl flex items-center justify-center shadow-md">
                  <Lock className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1F2937] font-[Outfit]">
                  {changePinStep === 'old' ? 'Enter Current PIN' : changePinStep === 'new' ? 'Enter New PIN' : 'Confirm New PIN'}
                </h3>
                <p className="text-[#9CA3AF] text-sm mt-1">
                  {changePinStep === 'old' ? 'Verify your identity' : changePinStep === 'new' ? 'Choose a new 4-digit PIN' : 'Re-enter your new PIN'}
                </p>
                {/* Step indicator */}
                <div className="flex justify-center gap-2 mt-3">
                  {['old', 'new', 'confirm'].map((s, i) => (
                    <div key={s} className={`w-2 h-2 rounded-full transition-colors ${changePinStep === s ? 'bg-[#C2410C]' : i < ['old','new','confirm'].indexOf(changePinStep) ? 'bg-[#C2410C]/40' : 'bg-gray-200'}`} />
                  ))}
                </div>
              </div>
              <div className="flex justify-center gap-4 mb-4">
                {[0, 1, 2, 3].map((i) => (
                  <input
                    key={`${changePinStep}-${i}`} id={`change-pin-${i}`}
                    type="password" inputMode="numeric" maxLength={1}
                    value={getChangePinArr()[i]}
                    onChange={(e) => handleChangePinInput(i, e.target.value)}
                    onKeyDown={(e) => handleChangePinKeyDown(i, e)}
                    disabled={changePinLoading}
                    className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/20 focus:outline-none transition-all disabled:opacity-50 bg-[#FFFBF5]"
                    data-testid={`change-pin-input-${i}`}
                  />
                ))}
              </div>
              {changePinLoading && (
                <div className="flex justify-center"><Loader2 className="w-6 h-6 text-[#C2410C] animate-spin" /></div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Child Modal */}
      <AnimatePresence>
        {showAddChild && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 overflow-y-auto py-8"
            onClick={() => setShowAddChild(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative my-auto"
              data-testid="add-child-modal"
            >
              <button onClick={() => { setShowAddChild(false); setAddChildStep('basic'); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-testid="close-add-child">
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold text-[#1F2937] font-[Outfit] mb-1">Add Your Child</h2>
              <p className="text-sm text-[#9CA3AF] mb-5">Create a profile to start personalized learning</p>

              {addChildStep === 'basic' ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Child's Name</Label>
                    <Input value={newChild.name} onChange={(e) => setNewChild({ ...newChild, name: e.target.value })} placeholder="Enter name" className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-11" data-testid="child-name-input" />
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Date of Birth</Label>
                    <Input type="date" value={newChild.date_of_birth} onChange={(e) => setNewChild({ ...newChild, date_of_birth: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      min={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-11" data-testid="child-dob-input" />
                    {newChild.date_of_birth && (() => {
                      const age = Math.floor((new Date() - new Date(newChild.date_of_birth)) / 31557600000);
                      return age < 3 ? <p className="text-xs text-red-500 mt-1">Child must be at least 3 years old</p> : null;
                    })()}
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Gender</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        { id: 'girl', label: 'Girl' },
                        { id: 'boy', label: 'Boy' },
                        { id: '', label: 'Prefer not to say' },
                      ].map((g) => (
                        <button key={g.id} type="button" onClick={() => setNewChild({ ...newChild, gender: g.id })} data-testid={`gender-${g.id || 'skip'}`}
                          className={`py-2 rounded-xl font-semibold text-xs transition-all ${newChild.gender === g.id ? 'bg-[#C2410C] text-white shadow-md' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'}`}
                        >{g.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#4B5563] font-semibold mb-2 block text-sm flex items-center gap-1">
                        <GraduationCap className="w-3.5 h-3.5 text-blue-500" /> Board <span className="text-red-500">*</span>
                      </Label>
                      <Select value={newChild.board} onValueChange={(v) => setNewChild({ ...newChild, board: v })}>
                        <SelectTrigger data-testid="board-select" className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-10 text-xs">
                          <SelectValue placeholder="Select board" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { id: 'CBSE', label: 'CBSE' }, { id: 'ICSE', label: 'ICSE' },
                            { id: 'State Board', label: 'State Board' }, { id: 'International', label: 'International (IB/Cambridge)' },
                            { id: 'Other', label: 'Other' },
                          ].map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#4B5563] font-semibold mb-2 block text-sm flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5 text-green-500" /> Class <span className="text-red-500">*</span>
                      </Label>
                      <Select value={newChild.grade} onValueChange={(v) => setNewChild({ ...newChild, grade: v })}>
                        <SelectTrigger data-testid="grade-select" className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-10 text-xs">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i + 1} value={`Class ${i + 1}`}>{`Class ${i + 1}`}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Pick an Avatar</Label>
                    <div className="grid grid-cols-6 gap-2">
                      {AVATARS.map((av) => {
                        const Icon = av.icon;
                        return (
                          <button key={av.id} type="button" onClick={() => setNewChild({ ...newChild, avatar: av.id })} data-testid={`avatar-option-${av.id}`}
                            className={`flex flex-col items-center gap-1 p-1.5 rounded-xl transition-all ${newChild.avatar === av.id ? 'ring-2 ring-[#C2410C] bg-orange-50 scale-105' : 'hover:bg-gray-50'}`}
                          >
                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${av.gradient} flex items-center justify-center shadow-sm`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-[9px] text-[#9CA3AF]">{av.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Chat Language</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { id: 'auto', label: 'Auto' }, { id: 'english', label: 'English' }, { id: 'hindi', label: 'Hindi' },
                        { id: 'hinglish', label: 'Hinglish' }, { id: 'tamil', label: 'Tamil' }, { id: 'telugu', label: 'Telugu' },
                        { id: 'bengali', label: 'Bengali' }, { id: 'marathi', label: 'Marathi' },
                      ].map((lang) => (
                        <button key={lang.id} type="button" onClick={() => setNewChild({ ...newChild, language_preference: lang.id })} data-testid={`lang-${lang.id}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${newChild.language_preference === lang.id ? 'bg-[#C2410C] text-white' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'}`}
                        >{lang.label}</button>
                      ))}
                    </div>
                  </div>
                  <Button type="button" onClick={() => {
                    if (!newChild.name.trim()) { toast.error("Enter your child's name"); return; }
                    if (!newChild.date_of_birth) { toast.error('Enter date of birth'); return; }
                    if (!newChild.avatar) { toast.error('Pick an avatar'); return; }
                    setAddChildStep('interests');
                  }} className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-xl py-5 font-bold" data-testid="next-to-interests-btn">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <button onClick={() => setAddChildStep('basic')} className="flex items-center gap-1 text-sm text-[#9CA3AF] hover:text-[#4B5563]" data-testid="back-to-basic">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <p className="text-sm text-[#4B5563] font-medium">What does {newChild.name} love? <span className="text-[#9CA3AF] font-normal">(optional)</span></p>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Interests</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Science', 'Math', 'History', 'Geography', 'Space', 'Animals', 'Technology', 'Art', 'Music', 'Sports', 'Reading', 'Cooking', 'Environment', 'Mythology', 'Coding', 'Puzzles'].map((item) => (
                        <button key={item} type="button" onClick={() => setNewChild(prev => ({
                          ...prev, interests: prev.interests.includes(item) ? prev.interests.filter(i => i !== item) : [...prev.interests, item]
                        }))} data-testid={`interest-${item.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            newChild.interests.includes(item) ? 'bg-rose-500 text-white shadow-md' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'
                          }`}>{item}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Hobbies</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Cricket', 'Drawing', 'Singing', 'Dancing', 'Swimming', 'Cycling', 'Chess', 'Gardening', 'Photography', 'Writing', 'Skating', 'Badminton', 'Painting', 'Crafts', 'Football', 'Yoga'].map((item) => (
                        <button key={item} type="button" onClick={() => setNewChild(prev => ({
                          ...prev, hobbies: prev.hobbies.includes(item) ? prev.hobbies.filter(i => i !== item) : [...prev.hobbies, item]
                        }))} data-testid={`hobby-${item.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            newChild.hobbies.includes(item) ? 'bg-indigo-500 text-white shadow-md' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'
                          }`}>{item}</button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleCreateChild} disabled={isCreating} className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-xl py-5 font-bold" data-testid="create-child-btn">
                    {isCreating ? 'Creating...' : 'Create Profile'}
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Child Modal */}
      <AnimatePresence>
        {showEditChild && editChild && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 overflow-y-auto py-8"
            onClick={() => setShowEditChild(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative my-auto"
              data-testid="edit-child-modal"
            >
              <button onClick={() => { setShowEditChild(false); setEditChildStep('basic'); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-testid="close-edit-child">
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold text-[#1F2937] font-[Outfit] mb-1">Edit Profile</h2>
              <p className="text-sm text-[#9CA3AF] mb-5">Update {editChild.name}'s details</p>

              {editChildStep === 'basic' ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Child's Name</Label>
                    <Input value={editChild.name} onChange={(e) => setEditChild({ ...editChild, name: e.target.value })} placeholder="Enter name" className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-11" data-testid="edit-child-name" />
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Date of Birth</Label>
                    <Input type="date" value={editChild.date_of_birth} onChange={(e) => setEditChild({ ...editChild, date_of_birth: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      min={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-11" data-testid="edit-child-dob" />
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Gender</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        { id: 'girl', label: 'Girl' },
                        { id: 'boy', label: 'Boy' },
                        { id: '', label: 'Prefer not to say' },
                      ].map((g) => (
                        <button key={g.id} type="button" onClick={() => setEditChild({ ...editChild, gender: g.id })} data-testid={`edit-gender-${g.id || 'skip'}`}
                          className={`py-2 rounded-xl font-semibold text-xs transition-all ${editChild.gender === g.id ? 'bg-[#C2410C] text-white shadow-md' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'}`}
                        >{g.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#4B5563] font-semibold mb-2 block text-sm flex items-center gap-1">
                        <GraduationCap className="w-3.5 h-3.5 text-blue-500" /> Board <span className="text-red-500">*</span>
                      </Label>
                      <Select value={editChild.board} onValueChange={(v) => setEditChild({ ...editChild, board: v })}>
                        <SelectTrigger data-testid="edit-board-select" className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-10 text-xs">
                          <SelectValue placeholder="Select board" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { id: 'CBSE', label: 'CBSE' }, { id: 'ICSE', label: 'ICSE' },
                            { id: 'State Board', label: 'State Board' }, { id: 'International', label: 'International (IB/Cambridge)' },
                            { id: 'Other', label: 'Other' },
                          ].map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#4B5563] font-semibold mb-2 block text-sm flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5 text-green-500" /> Class <span className="text-red-500">*</span>
                      </Label>
                      <Select value={editChild.grade} onValueChange={(v) => setEditChild({ ...editChild, grade: v })}>
                        <SelectTrigger data-testid="edit-grade-select" className="border-2 border-orange-200 focus:border-[#C2410C] rounded-xl h-10 text-xs">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i + 1} value={`Class ${i + 1}`}>{`Class ${i + 1}`}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Pick an Avatar</Label>
                    <div className="grid grid-cols-6 gap-2">
                      {AVATARS.map((av) => {
                        const Icon = av.icon;
                        return (
                          <button key={av.id} type="button" onClick={() => setEditChild({ ...editChild, avatar: av.id })} data-testid={`edit-avatar-${av.id}`}
                            className={`flex flex-col items-center gap-1 p-1.5 rounded-xl transition-all ${editChild.avatar === av.id ? 'ring-2 ring-[#C2410C] bg-orange-50 scale-105' : 'hover:bg-gray-50'}`}
                          >
                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${av.gradient} flex items-center justify-center shadow-sm`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-[9px] text-[#9CA3AF]">{av.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Chat Language</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { id: 'auto', label: 'Auto' }, { id: 'english', label: 'English' }, { id: 'hindi', label: 'Hindi' },
                        { id: 'hinglish', label: 'Hinglish' }, { id: 'tamil', label: 'Tamil' }, { id: 'telugu', label: 'Telugu' },
                        { id: 'bengali', label: 'Bengali' }, { id: 'marathi', label: 'Marathi' },
                      ].map((lang) => (
                        <button key={lang.id} type="button" onClick={() => setEditChild({ ...editChild, language_preference: lang.id })} data-testid={`edit-lang-${lang.id}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${editChild.language_preference === lang.id ? 'bg-[#C2410C] text-white' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'}`}
                        >{lang.label}</button>
                      ))}
                    </div>
                  </div>
                  <Button type="button" onClick={() => {
                    if (!editChild.name.trim()) { toast.error("Enter your child's name"); return; }
                    setEditChildStep('interests');
                  }} className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-xl py-5 font-bold" data-testid="edit-next-interests">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <button onClick={() => setEditChildStep('basic')} className="flex items-center gap-1 text-sm text-[#9CA3AF] hover:text-[#4B5563]" data-testid="edit-back-to-basic">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <p className="text-sm text-[#4B5563] font-medium">What does {editChild.name} love? <span className="text-[#9CA3AF] font-normal">(optional)</span></p>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Interests</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Science', 'Math', 'History', 'Geography', 'Space', 'Animals', 'Technology', 'Art', 'Music', 'Sports', 'Reading', 'Cooking', 'Environment', 'Mythology', 'Coding', 'Puzzles'].map((item) => (
                        <button key={item} type="button" onClick={() => setEditChild(prev => ({
                          ...prev, interests: prev.interests.includes(item) ? prev.interests.filter(i => i !== item) : [...prev.interests, item]
                        }))} data-testid={`edit-interest-${item.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            editChild.interests.includes(item) ? 'bg-rose-500 text-white shadow-md' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'
                          }`}>{item}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#4B5563] font-semibold mb-2 block text-sm">Hobbies</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Cricket', 'Drawing', 'Singing', 'Dancing', 'Swimming', 'Cycling', 'Chess', 'Gardening', 'Photography', 'Writing', 'Skating', 'Badminton', 'Painting', 'Crafts', 'Football', 'Yoga'].map((item) => (
                        <button key={item} type="button" onClick={() => setEditChild(prev => ({
                          ...prev, hobbies: prev.hobbies.includes(item) ? prev.hobbies.filter(i => i !== item) : [...prev.hobbies, item]
                        }))} data-testid={`edit-hobby-${item.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            editChild.hobbies.includes(item) ? 'bg-indigo-500 text-white shadow-md' : 'bg-gray-100 text-[#4B5563] hover:bg-gray-200'
                          }`}>{item}</button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleSaveChild} disabled={isSaving} className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-xl py-5 font-bold" data-testid="save-child-btn">
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Link Child Modal */}
      <AnimatePresence>
        {showLinkChild && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl" data-testid="link-child-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Link Child's Account</h3>
                <button onClick={() => { setShowLinkChild(false); setLinkCode(''); }} className="p-1 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                If your child registered on Yukti independently, enter their family code to link their profile to your account.
              </p>
              <div className="mb-4">
                <Label className="text-sm font-semibold text-gray-700 mb-1">Family Code</Label>
                <Input
                  data-testid="link-code-input"
                  value={linkCode}
                  onChange={e => setLinkCode(e.target.value.toUpperCase())}
                  placeholder="e.g. YK-A3X7"
                  className="text-center text-lg font-mono tracking-widest"
                  maxLength={7}
                />
              </div>
              <Button
                data-testid="link-child-submit-btn"
                onClick={handleLinkChild}
                disabled={linkLoading || !linkCode.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl py-5 font-semibold"
              >
                {linkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {linkLoading ? 'Linking...' : 'Link Account'}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close profile menu */}
      {showProfileMenu && <div className="fixed inset-0 z-30" onClick={() => setShowProfileMenu(false)} />}
    </div>
  );
}
